import { Component, inject, Input, OnChanges, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { AccountView } from '@api-proxies';
import { EntityCardSection } from '@enums/EntityCardSection';
import { EntityTypes } from '@enums/EntityTypes';
import { AppEditorFactory } from '@factories/app-editor-factory/app-editor-factory';
import { EditorType } from '@factories/app-editor-factory/enum/editor-type';
import { EntityTypeCustomFieldService } from '@http/clients/entity-type-custom-field.service';
import { customFieldPlacement, ICardConfig } from '@utils/custom-field-placement';
import { GenericCardComponent, ObjectExtensionsService, ValidatorType } from 'ngx-lib';

@Component({
    selector: 'app-accounts-single-overview',
    templateUrl: './accounts-single-overview.component.html',
    styleUrls: ['./accounts-single-overview.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [GenericCardComponent]
})
export class AccountsSingleOverviewComponent implements OnChanges {
    @ViewChild('mainInfoCard')
    mainInfoCard?: GenericCardComponent;

    @ViewChild('customInfoCard')
    customInfoCard?: GenericCardComponent;

    @Input()
    account?: AccountView;

    @Input()
    isReadOnly?: boolean;

    factory: AppEditorFactory;

    entityCardSection: typeof EntityCardSection;
    cardsConfig: ICardConfig;

    hasArchivedProductType = false;
    hasArchivedUsers = false;

    private readonly entityCustomFieldService = inject(EntityTypeCustomFieldService);
    private readonly objectExtensionService = inject(ObjectExtensionsService);

    constructor() {
        this.factory = new AppEditorFactory();
        this.cardsConfig = [];
        this.entityCardSection = EntityCardSection;
    }

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (
            this.account &&
            changes['account'] &&
            !this.objectExtensionService.isEqualTo(changes['account'].currentValue, changes['account'].previousValue)
        ) {
            this.cardsConfig = {
                [EntityCardSection.AccountMainInfo]: [
                    {
                        label: 'Name',
                        componentId: EditorType.textInput,
                        fieldName: 'name',
                        bindContext: this.account,
                        isReadOnly: this.isReadOnly,
                        validations: [
                            { type: ValidatorType.required },
                            { type: ValidatorType.string, args: { maxLength: 100 } }
                        ],
                        additionalInfo: {
                            shouldTrimSpaces: true
                        }
                    },
                    {
                        label: 'Website',
                        componentId: EditorType.link,
                        fieldName: 'webSite',
                        bindContext: this.account,
                        isReadOnly: this.isReadOnly,
                        validations: [{ type: ValidatorType.string, args: { maxLength: 100 } }]
                    },
                    {
                        label: 'Linkedin',
                        componentId: EditorType.link,
                        fieldName: 'linkedin',
                        bindContext: this.account,
                        isReadOnly: this.isReadOnly,
                        validations: [{ type: ValidatorType.string, args: { maxLength: 100 } }]
                    },
                    {
                        label: 'Type',
                        componentId: EditorType.companyType,
                        fieldName: 'accountTypeId',
                        bindContext: this.account,
                        isReadOnly: this.isReadOnly
                    },
                    {
                        label: 'Markets',
                        componentId: EditorType.entity,
                        fieldName: 'accountMarkets',
                        bindContext: this.account,
                        entityId: 'accountMarket',
                        isReadOnly: this.isReadOnly
                    },
                    {
                        label: 'Salesperson',
                        componentId: EditorType.entityUser,
                        fieldName: 'accountSalespersons',
                        bindContext: this.account,
                        isReadOnly: this.isReadOnly
                    },
                    {
                        label: 'Description',
                        componentId: EditorType.textarea,
                        fieldName: 'description',
                        bindContext: this.account,
                        isReadOnly: this.isReadOnly,
                        validations: [{ type: ValidatorType.string, args: { maxLength: 1000 } }]
                    }
                ],
                [EntityCardSection.AccountAdditionalInfo]: [
                    {
                        label: 'Product Types',
                        componentId: EditorType.entity,
                        fieldName: 'accountProductTypes',
                        entityId: 'accountProductType',
                        bindContext: this.account,
                        isReadOnly: this.isReadOnly,
                        additionalInfo: {
                            entityType: EntityTypes.Account
                        }
                    },
                    {
                        label: 'Building Product Types',
                        componentId: EditorType.accountBuildingProductType,
                        fieldName: 'accountBuildingProductTypes',
                        bindContext: this.account,
                        isReadOnly: this.isReadOnly,
                        additionalInfo: {
                            accountId: this.account.id
                        }
                    }
                ],
                [EntityCardSection.AccountCustomInfo]: []
            };

            if (!this.account.customFieldValues?.length)
                this.account.customFieldValues = await this.entityCustomFieldService.getCustomFieldsByEntityType(
                    EntityTypes.Account
                );

            this.account.customFieldValues.forEach((customField) => {
                customFieldPlacement(
                    this.cardsConfig,
                    customField,
                    EntityCardSection.AccountCustomInfo,
                    !!this.isReadOnly
                );
            });
            await this.checkArchivedItems(this.account);
        }
    }

    validate(): boolean {
        const mainInfoCardValid = this.mainInfoCard ? this.mainInfoCard.validate() : false;
        const customInfoCard = this.customInfoCard ? this.customInfoCard.validate() : true; // if no custom fields: set as valid

        const allValid = mainInfoCardValid && customInfoCard;

        return allValid;
    }

    private async checkArchivedItems(account: AccountView): Promise<void> {
        this.hasArchivedProductType = account.accountProductTypes?.some((c) => c.isActive === false) ?? false;
        this.hasArchivedUsers = account.accountSalespersons?.some((c) => c.isActive === false) ?? false;
    }
}
